import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, Eye, EyeOff, Star, RefreshCw } from "lucide-react";

export default function AdminRenewals() {
  const [campaigns, setCampaigns] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [deadline, setDeadline] = useState("");
  const [intro, setIntro] = useState("");

  const loadAll = () => {
    api.get("/admin/renewals/campaigns").then((r) => setCampaigns(r.data));
    api.get("/admin/renewals", { params: selected ? { campaign_id: selected } : {} }).then((r) => setItems(r.data));
  };
  useEffect(() => { loadAll(); }, [selected]);

  const launch = async () => {
    if (!year) { toast.error("Année requise"); return; }
    try {
      const r = await api.post("/admin/renewals/campaigns", { year: parseInt(year), deadline, intro_message: intro });
      toast.success(`Campagne lancée — ${r.data.sent} email(s) envoyé(s)`);
      setIntro(""); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  const togglePublic = async (rid) => {
    try { await api.patch(`/admin/renewals/${rid}/toggle-public`); loadAll(); }
    catch (err) { toast.error("Erreur"); }
  };

  return (
    <PortalLayout sidebar="admin">
      <h1 className="text-3xl font-bold text-navy md:text-4xl">Renouvellements adhésion</h1>
      <p className="mt-1 text-neutral-600">Lancez une campagne annuelle de renouvellement et suivez les retours.</p>

      <div className="mt-6 rounded-2xl border border-wine-100 bg-wine-50/40 p-5">
        <h2 className="text-sm font-bold text-navy">🚀 Lancer une nouvelle campagne</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <input type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="Année" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="campaign-year" />
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="campaign-deadline" />
          <button onClick={launch} className="inline-flex items-center justify-center gap-2 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="campaign-launch"><Send className="h-3.5 w-3.5" /> Envoyer aux membres</button>
        </div>
        <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={2} placeholder="Message d'introduction (optionnel)" className="mt-3 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
      </div>

      <h2 className="mt-8 text-sm font-bold uppercase text-neutral-600">Campagnes</h2>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {campaigns.map((c) => (
          <button key={c.id} onClick={() => setSelected(selected === c.id ? null : c.id)} className={`rounded-2xl border p-4 text-left transition-all ${selected === c.id ? "border-wine-500 bg-wine-50" : "border-neutral-100 bg-white hover:border-wine-300"}`} data-testid={`campaign-${c.id}`}>
            <div className="font-serif text-2xl font-bold text-wine-700">{c.year}</div>
            <div className="text-xs text-neutral-500">Envoyé : {c.sent_count} · Répondu : {c.responded} · Payé : {c.renewed_paid}</div>
          </button>
        ))}
      </div>

      <h2 className="mt-8 text-sm font-bold uppercase text-neutral-600">Réponses {selected ? "(filtré)" : "(toutes)"}</h2>
      <div className="mt-3 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs text-neutral-600">
            <tr><th className="px-4 py-2 text-left">Entreprise</th><th className="px-4 py-2 text-left">Année</th><th className="px-4 py-2 text-left">Renouvelle ?</th><th className="px-4 py-2 text-left">Note</th><th className="px-4 py-2 text-left">CA</th><th className="px-4 py-2 text-left">Paiement</th><th className="px-4 py-2 text-left">Commentaire</th><th className="px-4 py-2 text-left">Public</th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100" data-testid={`renewal-row-${r.id}`}>
                <td className="px-4 py-2 font-medium">{r.company_name}<div className="text-[10px] text-neutral-500">{r.contact_email}</div></td>
                <td className="px-4 py-2">{r.year}</td>
                <td className="px-4 py-2">{r.want_renew === true ? "✅ Oui" : r.want_renew === false ? "❌ Non" : "—"}</td>
                <td className="px-4 py-2">{r.rating ? <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{r.rating}/5</span> : "—"}</td>
                <td className="px-4 py-2 text-xs">{r.annual_revenue ? `${r.annual_revenue.toLocaleString("fr")} €` : "—"}</td>
                <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-600"}`}>{r.payment_status}</span></td>
                <td className="px-4 py-2 max-w-xs text-xs text-neutral-700">{r.comment || "—"}</td>
                <td className="px-4 py-2">
                  {r.comment && <button onClick={() => togglePublic(r.id)} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${r.public_comment ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`} data-testid={`toggle-public-${r.id}`}>
                    {r.public_comment ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />} {r.public_comment ? "Visible" : "Privé"}
                  </button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-neutral-500">Aucune réponse.</td></tr>}
          </tbody>
        </table>
      </div>
    </PortalLayout>
  );
}
